import {helpers} from '@growthbook/growthbook-react';

declare global {
  interface Window {
    __riseGbFeatures?: Promise<Response>;
  }
}

type FetchFeaturesCall = typeof helpers.fetchFeaturesCall;

/**
 * What happened to the feature request index.html started: `used` when the SDK took its response, `refetched` when
 * it failed or answered for another key and the SDK fetched again, `pending` while the SDK is still waiting on it
 * (what a timeout reports when the early request is slow), `unused` when the SDK never fetched (fresh localStorage
 * cache) or index.html didn't start one.
 */
export type GrowthBookPrefetch = 'used' | 'refetched' | 'pending' | 'unused';

export type EarlyFeatureRequest = {
  outcome: () => GrowthBookPrefetch;
  /** Puts the SDK's normal fetch back if the initial load didn't use it. Call once init settles. */
  release: () => void;
};

/**
 * index.html starts GrowthBook's feature request before the bundle loads (window.__riseGbFeatures), so the round trip
 * overlaps the bundle download instead of following it. This hands that response to the SDK's first fetch in place of
 * a new request, which leaves the SDK's own caching, refresh and error handling unchanged. Anything unexpected (the
 * request failed, a non-200, a different host or client key) falls back to the SDK's normal fetch. The early response
 * only ever answers the initial load: after a cache hit, `release` keeps a later refresh from getting it.
 */
export function adoptEarlyFeatureRequest(
  early: Promise<Response> | undefined = window.__riseGbFeatures,
  target: {fetchFeaturesCall: FetchFeaturesCall} = helpers,
): EarlyFeatureRequest {
  let outcome: GrowthBookPrefetch = 'unused';
  if (!early) {
    return {outcome: () => outcome, release: () => {}};
  }
  const fetchFeaturesCall = target.fetchFeaturesCall;
  const adopt: FetchFeaturesCall = args => {
    target.fetchFeaturesCall = fetchFeaturesCall;
    outcome = 'pending';
    const refetch = () => {
      outcome = 'refetched';
      return fetchFeaturesCall(args);
    };
    return early.then(res => {
      if (!res.ok || res.url !== `${args.host}/api/features/${args.clientKey}`) {
        return refetch();
      }
      outcome = 'used';
      return res;
    }, refetch);
  };
  target.fetchFeaturesCall = adopt;
  return {
    outcome: () => outcome,
    release: () => {
      if (target.fetchFeaturesCall === adopt) {
        target.fetchFeaturesCall = fetchFeaturesCall;
      }
    },
  };
}
